import { useCallback, useEffect, useMemo, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useForm } from 'react-hook-form';
import { v4 } from 'uuid';
import { logger } from 'common/utils/helpers';
import { addToastNotification } from 'redux/actions';
import { selectChartSet } from 'redux/selectors';
import { patchUserPreference, localChartPlaceholder } from 'redux/UserPreferences';

const defaultValues = {
  state: {
    query: {
      devices: [],
      time: { span: 'week' }
    },
    charts: Array.from({ length: 3 }, () => ({
      preferenceId: localChartPlaceholder.preferenceId,
      category: localChartPlaceholder.category,
      local: true
    }))
  }
};

function useChartSetForm({ preference, deviceId, onCreate, onUpdate }) {
  const preferenceRef = useRef(null);
  const dispatch = useDispatch();
  const state = useSelector((state) => selectChartSet(state).state);
  const methods = useForm({
    defaultValues: deviceId
      ? {
          ...defaultValues,
          state: {
            ...defaultValues.state,
            query: {
              ...defaultValues.state.query,
              devices: [deviceId]
            }
          }
        }
      : defaultValues
  });
  const { handleSubmit, reset, getValues, formState } = methods;
  const { isSubmitting } = formState;
  const onSubmit = useCallback(
    async (values) => {
      try {
        await dispatch(
          patchUserPreference({
            preferenceId: values.preferenceId,
            category: values.category,
            state: values.state
          })
        );
        dispatch(
          addToastNotification({
            id: v4(),
            title: values.name,
            description: 'successfully updated'
          })
        );
      } catch (error) {
        logger.error(error);
        dispatch(
          addToastNotification({
            id: v4(),
            title: values.name,
            description: 'failed to update.'
          })
        );
      }
    },
    [dispatch]
  );
  const handleUpdate = useMemo(() => handleSubmit(onSubmit), [handleSubmit, onSubmit]);
  const handleCreate = useCallback(() => {
    onCreate(getValues('state'));
  }, [onCreate, getValues]);

  useEffect(() => {
    // do not touch form while it's submitting
    if (isSubmitting) {
      return;
    }

    // wait for preference to load
    if (!preference) {
      return;
    }

    // merge preference with given device id
    const values = {
      ...preference,
      state: {
        ...preference.state,
        query: {
          ...preference.state.query,
          devices: deviceId ? [deviceId] : preference.state.query.devices
        }
      }
    };

    if (preference !== preferenceRef.current) {
      // hard reset form on preference change
      reset(values);
    } else {
      // soft reset form on device change
      reset(values, { keepDirty: true, keepDirtyValues: true });
    }

    preferenceRef.current = preference;
  }, [reset, isSubmitting, preference, deviceId]);

  useEffect(() => {
    if (preference && state) {
      // restore previous form values with saved state
      reset(
        {
          ...preference,
          state: {
            ...preference.state,
            ...state,
            query: {
              ...preference.state.query,
              ...state.query,
              devices: deviceId ? [deviceId] : state.query.devices ?? preference.state.query.devices
            }
          }
        },
        { keepDefaultValues: true }
      );

      // remove saved state from store
      onUpdate({
        preferenceId: preference.preferenceId,
        state: null
      });
    }
  }, [reset, onUpdate, preference, state, deviceId]);

  useEffect(
    () => () => {
      // store unsaved values to redux
      // values will be cleaned on preference change
      onUpdate({
        preferenceId: getValues('preferenceId'),
        state: getValues('state')
      });
    },
    [onUpdate, getValues]
  );

  return [methods, { handleCreate, handleUpdate }];
}

export default useChartSetForm;
